import { useState, type ReactNode } from "react";
import { Link, Navigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Button, cx, ErrorState, FullPageSpinner, Icon } from "@/components/ui";
import { formatDayMonth, timeAgo } from "@/lib/format";
import {
  findNiche,
  jurisdictionChip,
  SOURCE_TYPE_LABEL,
  useGrowthNiches,
  useGrowthProfile,
  useGrowthSources,
} from "@/lib/growth";
import type {
  GrowthCompetitor,
  GrowthCompetitorStatus,
  GrowthNiche,
  GrowthNicheCatalogue,
  GrowthProfile,
  GrowthSource,
  GrowthSourceList,
  GrowthSourceStatus,
} from "@/lib/types";

import { useCompetitorScanToasts } from "./dashboard/scan-toasts";
import { Breadcrumb, pillButton, WidgetError, WidgetLoading } from "./dashboard/ui";
import { AddCompetitorDialog, ProposeSourceDialog } from "./source-dialogs";

/** The status filter chips, in the frame's order. */
const FILTERS: GrowthSourceStatus[] = [
  "active",
  "proposed",
  "approved",
  "paused",
  "stale",
  "failing",
  "retired",
  "blocked",
];

interface Look {
  label: string;
  /** The pill's background and text. */
  pill: string;
  /** The dot on the filter chip. */
  dot: string;
}

const LOOK: Record<GrowthSourceStatus, Look> = {
  active: { label: "Active", pill: "bg-[#d9f0e6] text-[#129970]", dot: "bg-[#12a97e]" },
  proposed: { label: "Proposed", pill: "bg-[#e3edfc] text-[#2966cc]", dot: "bg-[#2966cc]" },
  approved: { label: "Approved", pill: "bg-[#dbf2f2] text-[#0d7885]", dot: "bg-[#0d7885]" },
  paused: { label: "Paused", pill: "bg-[#efecea] text-[#5c5452]", dot: "bg-[#5c5452]" },
  stale: { label: "Stale", pill: "bg-[#fce7c6] text-[#bf6900]", dot: "bg-[#bf6900]" },
  failing: { label: "Failing", pill: "bg-[#fde0dd] text-[#b8291f]", dot: "bg-[#b8291f]" },
  retired: { label: "Retired", pill: "bg-[#f3f1f0] text-[#857d78]", dot: "bg-[#857d78]" },
  blocked: { label: "Blocked", pill: "bg-[#332926] text-white", dot: "bg-[#332926]" },
};

/** A competitor's status, in the same colours as the source it resembles. */
const COMPETITOR_LOOK: Record<GrowthCompetitorStatus, Look> = {
  monitoring: { ...LOOK.active, label: "Monitoring" },
  pending: { ...LOOK.proposed, label: "Pending" },
  paused: LOOK.paused,
  invalid: { ...LOOK.failing, label: "Invalid" },
};

/** The two tables' columns (Source · Type · Topics · Jurisdiction · Last checked · Status), as in the frame. */
const COLUMNS =
  "md:grid md:grid-cols-[minmax(0,320fr)_minmax(0,150fr)_minmax(0,220fr)_minmax(0,130fr)_minmax(0,130fr)_minmax(0,120fr)] md:items-center";
const card = "rounded-2xl border border-border bg-surface shadow-[0px_2px_4px_0px_rgba(112,71,46,0.06)]";
const cellText = "text-[12.5px] leading-[1.45] text-fg-muted md:pr-3";
const failingText = "text-[#b8291f]";

/**
 * Sources (Figma 4641:58452): where the business's intelligence comes from. Its business type's sources
 * that reach it and the ones it proposed, each with what it covers, when it was last checked and its status
 * (filtered by the chips, kept in the URL), then its competitor watchlist. Opened from the dashboard's
 * "Update sources".
 */
export function SourcesPage() {
  const profile = useGrowthProfile();
  const niches = useGrowthNiches();

  if (profile.isPending) return <FullPageSpinner />;
  if (profile.isError) return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  if (profile.data.status !== "active") return <Navigate to="/growth-intelligence" replace />;
  return <Sources profile={profile.data} catalogue={niches.data} />;
}

function Sources({
  profile,
  catalogue,
}: {
  profile: GrowthProfile;
  catalogue: GrowthNicheCatalogue | undefined;
}) {
  const { can } = useOrganization();
  const sources = useGrowthSources();
  const [params, setParams] = useSearchParams();
  const asked = params.get("status");
  const filter = FILTERS.find((f) => f === asked) ?? null;
  const manage = can("growth:sources_manage");
  const niche = findNiche(catalogue, profile.niche_id);
  const [dialog, setDialog] = useState<"propose" | "competitor" | null>(null);
  const close = () => setDialog(null);
  useCompetitorScanToasts(profile);
  const choose = (status: GrowthSourceStatus | null) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (status) out.set("status", status);
        else out.delete("status");
        return out;
      },
      { replace: true },
    );

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumb current="Sources" />

      <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
        <div className="flex min-w-64 flex-1 flex-col gap-1">
          <h1 className="text-2xl font-bold text-fg">Sources</h1>
          <p className="text-[13.5px] leading-[1.8] text-fg-subtle">
            Where your intelligence comes from.
            {sources.data &&
              ` ${plural(sources.data.counts.all, "source")} and ${plural(profile.competitors.length, "competitor website")} for this business.`}
          </p>
        </div>
        {niche && (
          <Button
            unstyled
            requires="growth:sources_manage"
            onClick={() => setDialog("propose")}
            className={pillButton}
          >
            + Propose new source
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2.5 rounded-xl bg-muted px-4 py-3 text-fg-muted">
        <Icon name="alert-circle" size={18} className="shrink-0" />
        <p className="text-[13px] leading-[1.8]">
          Suggesting a source sends it for review — it doesn't become trusted or start monitoring
          automatically.
        </p>
      </div>

      {sources.isPending ? (
        <WidgetLoading label="Loading sources" />
      ) : sources.isError ? (
        <WidgetError error={sources.error} onRetry={() => void sources.refetch()} />
      ) : (
        <>
          <StatusFilters counts={sources.data.counts} selected={filter} onSelect={choose} />
          <SourcesTable list={sources.data} filter={filter} catalogue={catalogue} />
        </>
      )}

      <CompetitorWatchlist
        profile={profile}
        niche={niche}
        manage={manage}
        onAdd={() => setDialog("competitor")}
      />

      {dialog === "propose" && niche && (
        <ProposeSourceDialog niche={niche} catalogue={catalogue} onClose={close} />
      )}
      {dialog === "competitor" && niche && (
        <AddCompetitorDialog niche={niche} profile={profile} onClose={close} />
      )}
    </div>
  );
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

// ── the filter chips ────────────────────────────────────────────────────────

function StatusFilters({
  counts,
  selected,
  onSelect,
}: {
  counts: GrowthSourceList["counts"];
  selected: GrowthSourceStatus | null;
  onSelect: (status: GrowthSourceStatus | null) => void;
}) {
  return (
    <div role="group" aria-label="Filter sources by status" className="flex flex-wrap items-center gap-2">
      <FilterChip active={selected === null} count={counts.all} onClick={() => onSelect(null)}>
        All
      </FilterChip>
      {FILTERS.map((status) => (
        <FilterChip
          key={status}
          active={selected === status}
          count={counts[status]}
          dot={LOOK[status].dot}
          onClick={() => onSelect(status)}
        >
          {LOOK[status].label}
        </FilterChip>
      ))}
    </div>
  );
}

function FilterChip({
  active,
  count,
  dot,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  dot?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] leading-normal whitespace-nowrap transition-colors",
        active ? "border-fg bg-fg text-white" : "border-border bg-surface text-fg hover:bg-brand-50",
      )}
    >
      {dot && <span aria-hidden className={cx("size-1.5 rounded-full", dot)} />}
      <span className="font-semibold">{children}</span>
      <span className={active ? "text-[#ccc4bf]" : "text-fg-subtle"}>{count}</span>
    </button>
  );
}

// ── the sources table ───────────────────────────────────────────────────────

function SourcesTable({
  list,
  filter,
  catalogue,
}: {
  list: GrowthSourceList;
  filter: GrowthSourceStatus | null;
  catalogue: GrowthNicheCatalogue | undefined;
}) {
  const rows = filter ? list.sources.filter((s) => s.status === filter) : list.sources;
  return (
    <div role="table" aria-label="Sources" className="flex flex-col gap-5">
      <Header labels={["Source", "Type", "Topics", "Jurisdiction", "Last checked", "Status"]} />
      <div role="rowgroup" className={cx(card, "divide-y divide-border")}>
        {rows.length === 0 ? (
          <p className="px-5 py-8 text-center text-[13px] text-fg-subtle">
            {filter
              ? `No ${LOOK[filter].label.toLowerCase()} sources right now.`
              : "No sources cover your topics yet. Propose one, or follow more topics."}
          </p>
        ) : (
          rows.map((s) => <SourceRow key={s.id} source={s} catalogue={catalogue} />)
        )}
      </div>
    </div>
  );
}

function Header({ labels }: { labels: string[] }) {
  return (
    <div role="rowgroup">
      <div
        role="row"
        className={cx(
          "hidden px-5 text-[11px] leading-normal font-semibold text-fg-subtle uppercase",
          COLUMNS,
        )}
      >
        {labels.map((label, i) => (
          <span key={i} role="columnheader" className="md:pr-3">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function SourceRow({
  source: s,
  catalogue,
}: {
  source: GrowthSource;
  catalogue: GrowthNicheCatalogue | undefined;
}) {
  const line = explanation(s);
  return (
    <div role="row" className={cx("flex flex-col gap-1.5 px-5 py-3", COLUMNS)}>
      <div role="cell" className="flex min-w-0 flex-col gap-[3px] md:pr-3">
        <a
          href={s.url}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] leading-normal font-semibold break-words text-fg hover:underline"
        >
          {s.name}
        </a>
        {line && (
          <p
            className={cx(
              "text-[11.5px] leading-[1.45]",
              s.status === "failing" ? failingText : "text-fg-subtle",
            )}
          >
            {line}
          </p>
        )}
      </div>
      <Cell label="Type">{SOURCE_TYPE_LABEL[s.source_type]}</Cell>
      <Cell label="Topics">{s.topics.map((t) => t.label).join(", ") || "—"}</Cell>
      <Cell label="Jurisdiction">
        {s.jurisdictions.map((j) => jurisdictionChip(j, catalogue)).join(", ") || "—"}
      </Cell>
      <Cell label="Last checked">{lastChecked(s)}</Cell>
      <div role="cell" className="flex">
        <StatusPill look={LOOK[s.status]} />
      </div>
    </div>
  );
}

/** A table cell; on a phone, where the columns stack, it says what it is. */
function Cell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p role="cell" className={cellText}>
      <span className="text-fg-subtle md:hidden">{label}: </span>
      {children}
    </p>
  );
}

function StatusPill({ look }: { look: Look }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-[5px] rounded-full px-[9px] py-1 text-[11px] leading-normal font-semibold whitespace-nowrap",
        look.pill,
      )}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {look.label}
    </span>
  );
}

/** The line under a source's name: why it isn't simply active, in plain words (CGI-KB-003). */
function explanation(s: GrowthSource): string | null {
  switch (s.status) {
    case "approved":
      return `Approved ${formatDayMonth(s.status_since)} · monitoring starts at the next scheduled check`;
    case "proposed":
      return s.proposed_by_you
        ? `Suggested by you on ${formatDayMonth(s.status_since)} · waiting for review`
        : "Waiting for review";
    case "stale":
      return "Hasn't updated on its usual schedule. Insights from it are marked stale";
    case "paused":
      return s.note ?? "Paused by CortexGrip while its scope is reviewed";
    case "failing":
      return "Couldn't reach the source after 3 attempts. We're retrying";
    case "retired":
      return s.note ?? "Retired by CortexGrip. Past insights are kept and labelled";
    case "blocked":
      return s.note ?? "Not a permitted source";
    default:
      return null;
  }
}

function lastChecked(s: GrowthSource): string {
  switch (s.status) {
    case "proposed":
    case "blocked":
      return "—";
    case "paused":
      return `Paused ${formatDayMonth(s.status_since)}`;
    case "failing":
      return `Failed ${timeAgo(s.status_since)}`;
    case "retired":
      return `Retired ${formatDayMonth(s.status_since)}`;
    default:
      return s.last_checked_at ? timeAgo(s.last_checked_at) : "Not checked yet";
  }
}

// ── the competitor watchlist ────────────────────────────────────────────────

function CompetitorWatchlist({
  profile,
  niche,
  manage,
  onAdd,
}: {
  profile: GrowthProfile;
  niche: GrowthNiche | undefined;
  manage: boolean;
  onAdd: () => void;
}) {
  const used = profile.competitors.length;
  const max = profile.max_competitors;
  const full = used >= max;
  return (
    <section aria-labelledby="gi-watchlist" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3 pt-3">
        <div className="flex min-w-64 flex-1 flex-col gap-1">
          <h2 id="gi-watchlist" className="text-lg font-bold text-fg">
            Competitor watchlist
          </h2>
          <p className="text-[13px] text-fg-subtle">
            Public websites only. We never collect private data.{" "}
            {max > 0
              ? `${used} of ${max} competitor slots used on your plan.`
              : "Watching competitors isn't part of your plan."}
            {manage && full && (
              <>
                {" "}
                <Link to="/billing" className="font-semibold text-brand-500 hover:text-brand-600">
                  Upgrade to watch more
                </Link>
              </>
            )}
          </p>
        </div>
        {niche && (
          <Button
            unstyled
            requires="growth:sources_manage"
            disabledReason={full ? "Every competitor slot on your plan is in use." : undefined}
            onClick={onAdd}
            className="rounded-full border border-border bg-surface px-4 py-[9px] text-[13px] font-semibold whitespace-nowrap text-fg transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            + Add competitor
          </Button>
        )}
      </div>

      <div role="table" aria-label="Competitor watchlist" className="flex flex-col gap-5">
        <Header labels={["Competitor", "Website", "Watching", "", "Last checked", "Status"]} />
        <div role="rowgroup" className={cx(card, "divide-y divide-border")}>
          {profile.competitors.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-fg-subtle">
              You're not watching any competitors yet.
            </p>
          ) : (
            profile.competitors.map((c) => <CompetitorRow key={c.id} competitor={c} niche={niche} />)
          )}
        </div>
      </div>
    </section>
  );
}

function CompetitorRow({
  competitor: c,
  niche,
}: {
  competitor: GrowthCompetitor;
  niche: GrowthNiche | undefined;
}) {
  const watching = c.watch.map((id) => niche?.watch.find((o) => o.id === id)?.label ?? id).join(", ");
  return (
    <div role="row" className={cx("flex flex-col gap-1.5 px-5 py-3", COLUMNS)}>
      <div role="cell" className="flex min-w-0 flex-col gap-[3px] md:pr-3">
        <p className="text-[13px] leading-normal font-semibold break-words text-fg">{c.name}</p>
        {c.status === "pending" && (
          <p className="text-[11.5px] leading-[1.45] text-fg-subtle">
            Added {formatDayMonth(c.created_at)} · first check within 24 hours
          </p>
        )}
        {c.status === "invalid" && (
          <p className={cx("text-[11.5px] leading-[1.45]", failingText)}>
            {c.status_note ?? "We couldn't reach this address."}{" "}
            <a href={c.url} target="_blank" rel="noreferrer" className="font-semibold hover:underline">
              Check the URL →
            </a>
          </p>
        )}
      </div>
      <Cell label="Website">
        <a href={c.url} target="_blank" rel="noreferrer" className="break-all hover:underline">
          {c.domain}
        </a>
      </Cell>
      <Cell label="Watching">{watching || "—"}</Cell>
      <span role="cell" className="hidden md:block" />
      <Cell label="Last checked">{c.last_checked_at ? timeAgo(c.last_checked_at) : "Not checked yet"}</Cell>
      <div role="cell" className="flex">
        <StatusPill look={COMPETITOR_LOOK[c.status]} />
      </div>
    </div>
  );
}
